import { useState, useEffect, useRef } from 'react';
import {
  InputAdornment,
  makeStyles,
  Popover,
  Button,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Calendar, DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import UsualInput from './UsualInput';
import RangeInput from './RangeInput';
import { checkIsValidDate, parseStringToDate } from './helpers';
import TextField from '@material-ui/core/TextField';
import moment from 'moment-timezone';
import { getFormattedDate } from '../../../helpers/localize';
import './index.css';

const useStyles = makeStyles(() => ({
  popover: {
    width: 332,
    height: 332,
    maxHeight: 500,
  },
  paper: {
    maxWidth: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 500,
    cursor: 'default',
  },
  paperWithChanges: {
    cursor: 'not-allowed',
    '& .rdrCalendarWrapper': {
      cursor: 'default',
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
  },
  inputRoot: {
    display: 'flex',
    alignItems: 'center',
  },
  inputShouldRange: {
    display: 'flex',
    alignItems: 'center',
  },
  inputRightRoot: {
    width: 114,
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    fontSize: 13,
    fontWeight: 400,
  },
  dateRangeDelimiter: {
    margin: '0 10px',
  },
  popoverInputContainer: {
    display: 'flex',
    margin: 12,
    gap: 12,
  },
}));

function CalendarPopover({
                           date,
                           disabled,
                           onChange,
                           shouldRange,
                           updateAfterChanging,
                           width,
                           shouldReduceFontSize,
                           minDate,
                           maxDate,
                           disabledDates,
                           label,
                           shortView,
                           required,
                           error,
                           containerElement,
                           inputClass,
                           inputPlaceholderClass,
                         }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(date);
  const [pendingValue, setPendingValue] = useState(date);
  const [justOpened, setJustOpened] = useState(false);
  const [pendingState, setPendingState] = useState({
    selection: {
      startDate: new Date(),
      endDate: null,
      key: 'selection',
    },
  });

  const [state, setState] = useState({
    selection: {
      startDate: new Date(),
      endDate: null,
      key: 'selection',
    },
  });

  const { en: { btn } } = useSelector(state => state.i18n);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (shouldRange) {
      const currentStart = date?.[0];
      const currentEnd = date?.[1];
      const pendingStart = pendingState.selection.startDate;
      const pendingEnd = pendingState.selection.endDate;

      const currentStartTime = currentStart?.getTime?.() || null;
      const currentEndTime = currentEnd?.getTime?.() || null;
      const pendingStartTime = pendingStart?.getTime?.() || null;
      const pendingEndTime = pendingEnd?.getTime?.() || null;

      return (currentStartTime !== pendingStartTime) ||
        (currentEndTime !== pendingEndTime);
    } else {
      const currentTime = date?.getTime?.() || null;
      const pendingTime = pendingValue?.getTime?.() || null;
      return currentTime !== pendingTime;
    }
  };

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setJustOpened(true);
    // Reset the flag after a short delay to allow normal calendar interaction
    setTimeout(() => {
      setJustOpened(false);
    }, 300);
  };

  const handleClose = (event, reason) => {
    // Prevent closing by backdrop click if there are unsaved changes
    if (reason === 'backdropClick' && hasUnsavedChanges()) {
      return;
    }
    setAnchorEl(null);
  };

  const handleCancel = () => {
    // Reset to original values
    if (shouldRange) {
      setPendingState({
        selection: {
          startDate: date?.[0] || new Date(),
          endDate: date?.[1] || null,
          key: 'selection',
        },
      });
    } else {
      setPendingValue(date);
    }
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (shouldRange) {
      const s = pendingState.selection;
      if (checkIsValidDate(getFormattedDate(new Date(s.startDate)))
        && checkIsValidDate(getFormattedDate(new Date(s.endDate)))) {
        updateDateValue(s);
      }
    } else {
      if (checkIsValidDate(getFormattedDate(new Date(pendingValue)))) {
        updateDateValue(pendingValue);
      }
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const classes = useStyles();

  const handleDateChange = (newDate) => {
    // Ignore date changes if popover just opened to prevent accidental selection
    if (justOpened) {
      return;
    }

    if (shouldRange) {
      // Only update pending state, don't apply immediately
      setPendingState({ ...pendingState, ...newDate });
      setState({ ...state, ...newDate });
    } else {
      // Only update pending value, don't apply immediately
      setPendingValue(newDate);
      setValue(newDate);
    }
  };

  useEffect(() => {
    if (!shouldRange) {
      if (date) {
        setValue(date);
        setPendingValue(date);
      } else {
        setValue('');
        setPendingValue('');
      }
    } else {
      if (date) {
        const newDate = {
          selection: {
            startDate: date[0],
            endDate: date[1],
            key: 'selection',
          },
        };
        setState({ ...state, ...newDate });
        setPendingState({ ...pendingState, ...newDate });
      } else {
        const newDate = {
          selection: {
            startDate: null,
            endDate: null,
            key: 'selection',
          },
        };
        setState({ ...state, ...newDate });
        setPendingState({ ...pendingState, ...newDate });
      }
    }
  }, [date]);

  const handleInputDateChange = (value) => {
    // Allow partial input while typing
    if (!value || value === '') {
      if (shouldRange) {
        const newState = {
          ...pendingState,
          selection: {
            ...pendingState.selection,
            startDate: null,
          },
        };
        setState(newState);
        setPendingState(newState);
      } else {
        setValue('');
        setPendingValue('');
      }
      return;
    }

    // Only validate complete dates (MM/DD/YYYY format)
    if (value.length === 10) {
      const date = moment(value, 'MM/DD/YYYY', true);
      if (date.isValid()) {
        if (shouldRange) {
          const newState = {
            ...pendingState,
            selection: {
              ...pendingState.selection,
              startDate: date.toDate(),
            },
          };
          setState(newState);
          setPendingState(newState);
        } else {
          const newValue = date.toDate();
          setValue(newValue);
          setPendingValue(newValue);
        }
      }
    }
  };

  const handleRangeInputDateChange = (value) => {
    // Allow partial input while typing
    if (!value || value === '') {
      const newState = {
        ...pendingState,
        selection: {
          ...pendingState.selection,
          endDate: null,
        },
      };
      setState(newState);
      setPendingState(newState);
      return;
    }

    // Only validate complete dates (MM/DD/YYYY format)
    if (value.length === 10) {
      const dateMoment = moment(value, 'MM/DD/YYYY', true);
      if (dateMoment.isValid()) {
        const newState = {
          ...pendingState,
          selection: {
            ...pendingState.selection,
            endDate: dateMoment.toDate(),
          },
        };
        setState(newState);
        setPendingState(newState);
      }
    }
  };

  const updateDateValue = (s) => {
    if (shouldRange) {
      const firstData =
        parseStringToDate(getFormattedDate(new Date(s.startDate)));
      const secondData =
        parseStringToDate(getFormattedDate(new Date(s.endDate)));
      if (firstData > secondData) {
        onChange([secondData, firstData]);
      } else {
        onChange([firstData, secondData]);
      }
    } else {
      const data = parseStringToDate(getFormattedDate(new Date(s)));
      onChange(data);
    }
  };

  const zoomLevel = (window.outerWidth - 10) / window.innerWidth;

  return (
    <>
      {shouldRange && (
        <RangeInput
          error={error}
          disabled={disabled}
          required={required}
          shortView={shortView}
          updateAfterChanging={updateAfterChanging}
          date={date}
          shouldReduceFontSize={shouldReduceFontSize}
          label={label}
          onChange={onChange}
          handleClick={handleClick}
          handleClose={handleClose}
          width={width}
          inputClass={inputClass}
          inputPlaceholderClass={inputPlaceholderClass}
        />
      )}
      {!shouldRange && (
        <UsualInput
          error={error}
          disabled={disabled}
          required={required}
          date={date}
          minDate={minDate}
          shortView={shortView}
          label={label}
          shouldReduceFontSize={shouldReduceFontSize}
          onChange={onChange}
          handleClick={handleClick}
          handleClose={handleClose}
        />
      )}
      <Popover
        classes={{
          root: hasUnsavedChanges() ? `${classes.popover} ${classes.paperWithChanges}` : classes.popover,
          paper: classes.paper,
        }}
        id={id}
        open={open}
        anchorEl={containerElement ? containerElement.current : anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {(shortView || updateAfterChanging) && <div className={classes.popoverInputContainer}>
          <_DateInput
            value={
              shouldRange
                ? (pendingState ? pendingState.selection.startDate : null)
                : pendingValue
            }
            InputProps={{
              startAdornment: shouldRange ? (
                <InputAdornment position="start">
                  {'from'}
                </InputAdornment>
              ) : null,
            }}
            updateValue={handleInputDateChange}
            handleClose={handleClose}
          />
          {shouldRange && (
            <_DateInput
              value={pendingState ? pendingState.selection.endDate : null}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {'to'}
                  </InputAdornment>
                ),
              }}
              updateValue={handleRangeInputDateChange}
              handleClose={handleClose}
            />
          )}
        </div>}
        {shouldRange && (
          <DateRangePicker
            maxDate={maxDate ?? undefined}
            months={2}
            direction="vertical"
            fixedHeight={true}
            scroll={{
              enabled: true,
              monthHeight: zoomLevel < 1 ? 320 : 230,
              longMonthHeight: zoomLevel < 1 ? 350 : 250,
            }}
            ranges={[pendingState.selection]}
            onChange={handleDateChange}
            showSelectionPreview
            moveRangeOnFirstSelection={false}
            disabledDates={disabledDates}
          />
        )}
        {!shouldRange && (
          <div
            onMouseDown={(e) => {
              // Prevent immediate interaction if popover just opened
              if (justOpened) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              if (justOpened) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Calendar
              maxDate={maxDate ?? undefined}
              minDate={minDate ?? undefined}
              onChange={handleDateChange}
              date={pendingValue}
              disabledDates={disabledDates}
            />
          </div>
        )}

        {/* Buttons Container */}
        <div className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            style={{ height: 36 }}
          >
            {btn?.cancel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            style={{ height: 36 }}
          >
            {btn?.apply || 'Apply'}
          </Button>
        </div>
      </Popover>
    </>
  );
}

CalendarPopover.propTypes = {
  width: PropTypes.number,
  shouldReduceFontSize: PropTypes.bool,
  shortView: PropTypes.bool,
  disabled: PropTypes.bool,
  shouldRange: PropTypes.bool,
  date: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  label: PropTypes.string,
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
};

CalendarPopover.defaultProps = {
  disabled: false,
  shouldRange: false,
  shortView: false,
  date: null,
  maxDate: null,
  minDate: null,
  onChange: undefined,
  width: undefined,
  disabledDates: undefined,
  label: undefined,
  shouldReduceFontSize: false,
};

export default CalendarPopover;


function _DateInput({
                      value,
                      updateValue,
                      InputProps,
                      handleClose,
                    }) {
  const [_value, setValue] = useState(value
    ? getFormattedDate(new Date(value))
    : '');
  const inputRef = useRef();

  // Sync internal state with external value changes
  useEffect(() => {
    const newValue = value
      ? getFormattedDate(new Date(value))
      : '';
    setValue(newValue);
  }, [value]);

  const handleChange = (e) => {
    const input = e.target.value;
    const cursorPosition = e.target.selectionStart;

    // Remove all non-digits
    const digitsOnly = input.replace(/[^\d]/g, '');

    // Format the digits as MM/DD/YYYY
    let formatted = '';
    for (let i = 0; i < digitsOnly.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += digitsOnly[i];
    }

    setValue(formatted);
    updateValue(formatted);

    // Calculate new cursor position
    setTimeout(() => {
      if (inputRef.current) {
        let newPos = cursorPosition;

        // Adjust cursor position based on slashes
        const slashesBeforeCursor = (input.substring(0, cursorPosition).match(/\//g) || []).length;
        const slashesInFormatted = (formatted.substring(0, cursorPosition).match(/\//g) || []).length;

        if (slashesInFormatted > slashesBeforeCursor) {
          newPos = cursorPosition + (slashesInFormatted - slashesBeforeCursor);
        }

        // Don't go past the end of the string
        newPos = Math.min(newPos, formatted.length);
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <TextField
      inputRef={inputRef}
      style={{ flex: '1 1 auto' }}
      placeholder={'MM/DD/YYYY'}
      variant="outlined"
      value={_value}
      onChange={handleChange}
      inputProps={{ maxLength: 10 }}
      InputProps={InputProps}
      KeyboardButtonProps={{ disabled: true, style: { display: 'none' } }}
      onKeyDown={handleKeyDown}
    />
  );
}
